import { useState } from "react";
import { http, formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { UploadCloud } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from "@/components/ui/dialog";

export default function BulkImportLearners({ onDone }) {
  const [open, setOpen] = useState(false);
  const [text, setText] = useState("");
  const [busy, setBusy] = useState(false);

  const parsed = text
    .split(/[\n,]+/)
    .map((s) => s.trim())
    .filter((s) => s.length > 0 && s.length <= 80);

  const submit = async (e) => {
    e.preventDefault();
    if (parsed.length === 0) {
      toast.error("Paste at least one chart name.");
      return;
    }
    setBusy(true);
    try {
      const r = await http.post("/learners/bulk", { chart_names: parsed });
      toast.success(
        `Added ${r.data.inserted} learner${r.data.inserted === 1 ? "" : "s"}` +
        (r.data.skipped > 0 ? ` · skipped ${r.data.skipped} duplicate` + (r.data.skipped === 1 ? "" : "s") : "")
      );
      setText("");
      setOpen(false);
      onDone?.();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || "Bulk import failed");
    } finally {
      setBusy(false);
    }
  };

  return (
    <>
      <Button
        variant="outline"
        onClick={() => setOpen(true)}
        className="rounded-full h-11 px-4"
        data-testid="bulk-import-button"
      >
        <UploadCloud className="w-4 h-4 mr-2" /> Bulk import
      </Button>

      <Dialog open={open} onOpenChange={(o) => { setOpen(o); if (!o) setText(""); }}>
        <DialogContent className="sm:max-w-lg">
          <DialogHeader>
            <DialogTitle className="font-display">Bulk import learners</DialogTitle>
            <DialogDescription>
              Paste one chart name per line (or separate with commas). Use initials or codes — no PHI.
              Duplicates are skipped automatically.
            </DialogDescription>
          </DialogHeader>
          <form onSubmit={submit} className="space-y-4">
            <div>
              <Label htmlFor="bulk-names">Chart names</Label>
              <Textarea
                id="bulk-names"
                rows={8}
                value={text}
                onChange={(e) => setText(e.target.value)}
                placeholder={"JS\nAM\nLearner A\nLearner B, Learner C"}
                className="mt-1.5 rounded-xl font-mono text-sm"
                data-testid="bulk-import-textarea"
                autoFocus
              />
              <div className="text-[11px] text-slate-500 mt-1.5" data-testid="bulk-import-count">
                {parsed.length === 0
                  ? "0 valid names detected"
                  : `${parsed.length} name${parsed.length === 1 ? "" : "s"} ready to import`}
              </div>
            </div>
            <DialogFooter>
              <Button
                type="submit"
                disabled={busy || parsed.length === 0}
                className="rounded-full"
                data-testid="bulk-import-submit"
              >
                {busy ? "Importing…" : `Import ${parsed.length || ""} learner${parsed.length === 1 ? "" : "s"}`}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
    </>
  );
}
